import { Component, inject, OnInit } from '@angular/core';
import { AppConfigService } from 'app/core/config/app-config.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';

import { WinService } from '@app/services/win.service';
import { environment } from '@env/environment';

export interface IHaveAWinDialogData {
  studentId: number;
  studentCourseId?: number;
  studentCourseStepId?: number;
  /** Resolved company branding win_header URL. Falls back to client folder, then base asset. */
  winHeaderImage?: string;
}

const WIN_HEADINGS = [
  "That's wonderful",
  'Wow',
  'Very well done',
  "That's awesome",
  'That sounds great',
];

const WIN_SUBHEADINGS = [
  'Please share your win',
  'Tell us all about it',
];

@Component({
  selector: 'app-i-have-a-win-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    TranslateModule,
  ],
  styles: [`
    :host {
      display: block;
    }
    .win-header-img {
      width: 100%;
      height: 180px;
      object-fit: cover;
      display: block;
      border-radius: 4px 4px 0 0;
    }
    .win-body {
      padding: 24px 24px 0;
    }
    .win-heading {
      font-size: 28px;
      font-weight: 800;
      line-height: 1.2;
      margin: 0 0 6px;
      color: inherit;
    }
    .win-subheading {
      font-size: 15px;
      font-weight: 400;
      margin: 0 0 16px;
      opacity: 0.65;
    }
  `],
  template: `
    <!-- Full-bleed header image -->
    <img
      class="win-header-img"
      [src]="winHeaderUrl"
      (error)="onImageError()"
      alt=""
      aria-hidden="true"
    />

    <!-- Headings inside mat-dialog-content-like area -->
    <div class="win-body">
      <p class="win-heading">{{ heading }}!</p>
      <p class="win-subheading">{{ subheading }}</p>
    </div>

    <mat-dialog-content class="!pt-0">
      <mat-form-field appearance="outline" class="w-full">
        <textarea matInput rows="6" [(ngModel)]="message" [disabled]="saving"
                  placeholder="{{ 'win.yourWin' | translate }}"></textarea>
      </mat-form-field>
    </mat-dialog-content>

    <mat-dialog-actions align="end" class="gap-2 px-6 pb-4">
      <button mat-stroked-button type="button" [disabled]="saving" (click)="dialogRef.close()">
        {{ 'win.cancel' | translate }}
      </button>
      <button mat-flat-button color="primary" type="button"
              [disabled]="saving || !message.trim()" (click)="submit()">
        <mat-spinner *ngIf="saving" diameter="20" class="inline-block mr-2"></mat-spinner>
        {{ 'win.send' | translate }}
      </button>
    </mat-dialog-actions>
  `,
})
export class IHaveAWinDialogComponent implements OnInit {
  readonly appConfig = inject(AppConfigService);
  dialogRef = inject(MatDialogRef<IHaveAWinDialogComponent>);
  data = inject<IHaveAWinDialogData>(MAT_DIALOG_DATA);
  private _winService = inject(WinService);
  private _snack = inject(MatSnackBar);

  message = '';
  saving = false;
  heading = '';
  subheading = '';
  winHeaderUrl = '';

  ngOnInit(): void {
    this.heading    = WIN_HEADINGS[Math.floor(Math.random() * WIN_HEADINGS.length)];
    this.subheading = WIN_SUBHEADINGS[Math.floor(Math.random() * WIN_SUBHEADINGS.length)];

    // Image priority: company setting → client folder → base asset (via onerror)
    this.winHeaderUrl = this.data.winHeaderImage
      || `assets/_clients/${this.appConfig.get('client')}/bg-winning.png`;
  }

  onImageError(): void {
    if (this.winHeaderUrl !== 'assets/bg-winning.png') {
      this.winHeaderUrl = 'assets/bg-winning.png';
    }
  }

  submit(): void {
    const text = this.message.trim();
    if (!text) return;
    this.saving = true;
    this._winService
      .saveWin(this.data.studentId, {
        message: text,
        subject: 'I have a win!',
        student_course_id: this.data.studentCourseId,
        student_course_step_id: this.data.studentCourseStepId,
        message_type: 'student_win',
      })
      .subscribe({
        next: () => {
          this._snack.open('Thanks — your supervisor has been notified.', 'Close', { duration: 4000 });
          this.dialogRef.close(true);
        },
        error: (e) => {
          this.saving = false;
          this._snack.open(typeof e === 'string' ? e : 'Could not send. Try again.', 'Close', { duration: 5000 });
        },
      });
  }
}
